// React
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";

// MUI
import {
  Alert,
  AlertTitle,
  Box,
  List,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { Grid2 as Grid } from "@mui/material";
import { CameraAltOutlined } from "@mui/icons-material";

// QR Components
import { Scanner } from "@yudiel/react-qr-scanner";
import { QRCloseButton } from "../QRCloseButton";
import processQRData from "../ProcessQRData";
import { QR_STATES } from "Constants/QRConstants";
import { useQR } from "../QRContext";
import { QRCodeReader } from "./QRCodeReader";

// Components
import { MNButton } from "Common/Components";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Styles
import styles from "../styles";

// Time
import { FORTY_FIVE_SECONDS } from "Constants/TimeConstants";

/**
 * @brief QR Code Scanner dialog for live scanning with a mobile or desktop/laptop camera. Times out after 45 seconds of nothing scanned.
 */
export const QRCodeScanner = () => {
  const { t } = useTranslation();
  const { setQRState, setQRJSON } = useQR();

  const fireFoxAsked = useRef(false);
  const askingRef = useRef(false);

  const [permission, setPermission] = useState("");
  const [isNotMobile, setIsNotMobile] = useState(false);
  const [uploadInstead, setUploadInstead] = useState(false);

  const timerId = useRef(null);
  const dispatch = useDispatch();

  const timeLimitScan = () => {
    if (!timerId.current) {
      timerId.current = setTimeout(() => {
        setQRState(QR_STATES.FAIL);
        timerId.current = null;
      }, FORTY_FIVE_SECONDS);
    }
  };

  const stopTimer = () => {
    if (timerId.current) {
      clearTimeout(timerId.current);
      timerId.current = null;
    }
  };

  const onUploadInstead = () => {
    stopTimer();
    setUploadInstead(true);
  };

  useEffect(() => {
    return () => stopTimer(); // stop timer on component dismount
  }, []);

  useEffect(() => {
    const checkNotMobile = async () => {
      if (navigator.userAgentData) {
        setIsNotMobile(!navigator.userAgentData.mobile);
      } else {
        setIsNotMobile(
          !/Mobile|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
            navigator.userAgent
          )
        );
      }
    };

    const askForPerm = async () => {
      if (askingRef.current || fireFoxAsked.current) {
        return;
      }
      askingRef.current = true;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        stream.getVideoTracks().forEach(function (track) {
          track.stop();
        });

        setPermission("granted");
      } catch (error) {
        dispatch(
          openSnackbar({
            message: t("qrcode.cameraAccessDenied"),
            severity: "error",
          })
        );
        setPermission("denied");
      } finally {
        fireFoxAsked.current =
          /(?:android.*firefox|firefox.*android|fxios)/i.test(
            navigator.userAgent
          );

        askingRef.current = false;
      }
    };

    const handlePermissions = async () => {
      const queryResult = await navigator.permissions.query({ name: "camera" });

      if (queryResult.state === "granted") {
        // if granted, check device type for camera mirroring, for cases with one user facing camera on a laptop
        checkNotMobile();
        setPermission("granted");
      }
      if (queryResult.state === "prompt") {
        // override asking for video permission to decide which dialog to show (warning about no camera access or scanner)
        askForPerm();
      }
    };

    handlePermissions();
  }, [permission, dispatch, t]);

  const handleDetection = (codes) => {
    if (codes?.length > 0) {
      stopTimer();

      const hcJSON = processQRData(codes[0].rawValue);

      if (hcJSON) {
        setQRJSON(hcJSON);
        setQRState(QR_STATES.SUCCESS);
      } else {
        setQRState(QR_STATES.FAIL);
      }
    }
  };

  const permissionBasedRender = () => {
    if (permission === "granted") {
      timeLimitScan();
      return (
        <Stack alignItems="center" spacing={0.5} sx={{ maxWidth: "20rem" }}>
          <Scanner
            onScan={handleDetection}
            components={{ finder: false }}
            formats={["qr_code"]}
            styles={styles.scanner(isNotMobile)}
            sound={false}
            allowMultiple={false}
          >
            <Grid sx={styles.scannerTargetGrid}>
              <Box sx={styles.scannerTarget} />
            </Grid>
          </Scanner>
          <Stack spacing={0.25} sx={styles.instructionsBox}>
            <Typography sx={styles.instructionsHeader}>
              {t("qrcode.detailedInstructionsPrompt")}
            </Typography>
            <Typography variant="subtitle2" color="textSecondary">
              {isNotMobile
                ? t("qrcode.additionalInstructionsPrompt")
                : t("qrcode.bulletPointsIntro")}
            </Typography>

            <List dense sx={{ p: 0, pl: 3, listStyleType: "disc" }}>
              <ListItemText
                sx={styles.scanHelpInstruction}
                primary={t("qrcode.instructionSteady")}
              />
              {isNotMobile ? (
                <ListItemText
                  sx={styles.scanHelpInstruction}
                  primary={t("qrcode.instructionLightingDesktop")}
                />
              ) : (
                <ListItemText
                  sx={styles.scanHelpInstruction}
                  primary={t("qrcode.instructionLightingMobile")}
                />
              )}
              <ListItemText
                sx={styles.scanHelpInstruction}
                primary={t("qrcode.instructionClean")}
              />
            </List>

            <MNButton sx={styles.uploadImageInstead} onClick={onUploadInstead}>
              {t("qrcode.uploadImageInstead")}
            </MNButton>
          </Stack>
          <Typography sx={styles.disclaimer}>
            {t("qrcode.disclaimer")}
          </Typography>
          <QRCloseButton onClose={() => stopTimer()} />
        </Stack>
      );
    } else {
      return (
        <Stack alignItems="center" spacing={2}>
          <Alert
            severity="warning"
            iconMapping={{
              warning: <CameraAltOutlined fontSize="inherit" />,
            }}
          >
            <AlertTitle>{t("qrcode.allowCameraAccess")}</AlertTitle>
            <Stack spacing={1}>
              <Typography sx={styles.cameraPermissionInstruction}>
                {t("qrcode.cameraPermissionInstructions")}
              </Typography>
              <Typography sx={styles.cameraPermissionInstruction}>
                {t("qrcode.uploadImageInsteadInstructions")}
              </Typography>
            </Stack>
          </Alert>
          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={{ xs: 1.5, sm: 2 }}
            alignItems="center"
          >
            <QRCloseButton />
            <MNButton sx={styles.mainActionButton} onClick={onUploadInstead}>
              {t("qrcode.uploadImage")}
            </MNButton>
          </Stack>
        </Stack>
      );
    }
  };

  const scanOrUpload = () => {
    if (uploadInstead) {
      return <QRCodeReader />;
    } else {
      return permissionBasedRender();
    }
  };

  return scanOrUpload();
};
